import {useEffect, useRef, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {
  ControlRow,
  FELT,
  FeltBoard,
  FeltButton,
  FeltDefs,
  FeltTag,
  PokeGroup,
  Readout,
  WidgetConcept,
  WidgetTitle,
  useFeltIds,
  usePoke,
} from './felt';

/**
 * A plush die you can roll, next to a felt histogram of every roll so far.
 * With a few rolls the bars are lumpy; with many they settle toward the
 * dashed 1/6 line — the law of large numbers. Poking a bar makes it boing
 * and tags it with its count and share.
 */

const FACES = 6;
/** How long a single roll tumbles before settling, in ms. */
const TUMBLE_MS = 600;
/** How often the face flickers while tumbling, in ms. */
const FLICKER_MS = 70;

/** Pip centers for each face, in the die's 140×140 viewBox. */
export const DIE_PIPS: Record<
  number,
  ReadonlyArray<readonly [number, number]>
> = {
  1: [[70, 70]],
  2: [
    [42, 42],
    [98, 98],
  ],
  3: [
    [40, 40],
    [70, 70],
    [100, 100],
  ],
  4: [
    [42, 42],
    [98, 42],
    [42, 98],
    [98, 98],
  ],
  5: [
    [40, 40],
    [100, 40],
    [70, 70],
    [40, 100],
    [100, 100],
  ],
  6: [
    [42, 36],
    [98, 36],
    [42, 70],
    [98, 70],
    [42, 104],
    [98, 104],
  ],
};

/** Histogram geometry, in its 300×180 viewBox. */
const BAR_W = 34;
const BAR_GAP = 12;
const BAR_LEFT = 24;
const BAR_FLOOR = 150;
/** Proportion that fills a bar to the top of the chart. */
const MAX_SHARE = 0.4;
const BAR_MAX_H = 130;

const tumble = keyframes`
  0% { transform: rotate(0) translateY(0); }
  20% { transform: rotate(-18deg) translateY(-18px) scale(1.04, 0.96); }
  45% { transform: rotate(14deg) translateY(-6px); }
  70% { transform: rotate(-8deg) translateY(-10px); }
  100% { transform: rotate(0) translateY(0); }
`;

const DieWrap = styled.button<{$isTumbling: boolean}>`
  display: block;
  margin: 0 auto;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${({$isTumbling}) =>
    $isTumbling &&
    css`
      animation: ${tumble} ${TUMBLE_MS}ms ease-in-out;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) 2fr;
  align-items: center;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/** A uniformly random face, 1–6. */
function randomFace(): number {
  return 1 + Math.floor(Math.random() * FACES);
}

/**
 * Renders the die roller widget (see module comment).
 *
 * @return The widget card.
 * @throws Never.
 */
export function DieRoller() {
  const ids = useFeltIds();
  const poke = usePoke();
  const [counts, setCounts] = useState<number[]>(() => Array(FACES).fill(0));
  const [face, setFace] = useState(5);
  const [isTumbling, setIsTumbling] = useState(false);
  const timersRef = useRef<number[]>([]);

  useEffect(
    () => () => {
      timersRef.current.forEach(timer => {
        window.clearTimeout(timer);
      });
    },
    [],
  );

  const record = (faces: readonly number[]) => {
    setCounts(previous => {
      const next = [...previous];
      faces.forEach(value => {
        next[value - 1] = (next[value - 1] ?? 0) + 1;
      });
      return next;
    });
  };

  const roll = (times: number) => {
    if (isTumbling) {
      return;
    }
    const results = Array.from({length: times}, randomFace);
    setIsTumbling(true);
    for (let elapsed = 0; elapsed < TUMBLE_MS; elapsed += FLICKER_MS) {
      timersRef.current.push(
        window.setTimeout(() => {
          setFace(randomFace());
        }, elapsed),
      );
    }
    timersRef.current.push(
      window.setTimeout(() => {
        setFace(results[results.length - 1] ?? 1);
        record(results);
        setIsTumbling(false);
      }, TUMBLE_MS),
    );
  };

  const total = counts.reduce((sum, count) => sum + count, 0);
  const pokedIndex = poke.lastId?.startsWith('bar-')
    ? Number(poke.lastId.slice('bar-'.length)) - 1
    : undefined;
  const pokedShare =
    pokedIndex === undefined || total === 0
      ? 0
      : (counts[pokedIndex] ?? 0) / total;
  const shareLineY = BAR_FLOOR - (BAR_MAX_H * (1 / FACES)) / MAX_SHARE;

  return (
    <FeltBoard>
      <WidgetTitle>Roll the plush die</WidgetTitle>
      <WidgetConcept>
        The more you roll, the closer each face gets to 1/6 (the law of large
        numbers).
      </WidgetConcept>
      <Layout>
        <DieWrap
          type="button"
          $isTumbling={isTumbling}
          aria-label={`Roll the die. It shows ${face}.`}
          onClick={() => {
            roll(1);
          }}
        >
          <svg viewBox="0 0 140 140" width={140} height={140} aria-hidden>
            <defs>
              <FeltDefs ids={ids} />
              <radialGradient id={`${ids.fuzz}-die`} cx="38%" cy="30%" r="80%">
                <stop offset="0%" stopColor={FELT.creamLight} />
                <stop offset="65%" stopColor={FELT.cream} />
                <stop offset="100%" stopColor={FELT.creamDark} />
              </radialGradient>
            </defs>
            <rect
              x={10}
              y={10}
              width={120}
              height={120}
              rx={32}
              fill={`url(#${ids.fuzz}-die)`}
              filter={`url(#${ids.fuzz})`}
            />
            <rect
              x={20}
              y={20}
              width={100}
              height={100}
              rx={24}
              fill="none"
              stroke={FELT.cardinal}
              strokeWidth={1.8}
              strokeDasharray="5 4"
              opacity={0.4}
            />
            {(DIE_PIPS[face] ?? []).map(([cx, cy]) => (
              <g key={`${cx}-${cy}`}>
                <circle cx={cx} cy={cy} r={10} fill={FELT.cardinal} />
                <circle
                  cx={cx - 3}
                  cy={cy - 3}
                  r={3}
                  fill="#fff"
                  opacity={0.25}
                />
              </g>
            ))}
          </svg>
        </DieWrap>

        <svg
          viewBox="0 0 300 180"
          style={{display: 'block', width: '100%'}}
          role="img"
          aria-label={`Histogram of ${total} rolls: ${counts
            .map((count, index) => `${index + 1}: ${count}`)
            .join(', ')}`}
        >
          {counts.map((count, index) => {
            const faceValue = index + 1;
            const share = total === 0 ? 0 : count / total;
            const height = Math.min(BAR_MAX_H, (BAR_MAX_H * share) / MAX_SHARE);
            const x = BAR_LEFT + index * (BAR_W + BAR_GAP);
            const barId = `bar-${faceValue}`;
            return (
              <PokeGroup
                key={faceValue}
                $animation="boing"
                $nonce={poke.nonceFor(barId)}
                $origin="center bottom"
                onClick={() => {
                  poke.poke(barId);
                }}
              >
                {/* Full-height hit area, so even an empty bar can be poked. */}
                <rect
                  x={x}
                  y={BAR_FLOOR - BAR_MAX_H}
                  width={BAR_W}
                  height={BAR_MAX_H}
                  fill="transparent"
                />
                <rect
                  x={x}
                  y={BAR_FLOOR - height}
                  width={BAR_W}
                  height={Math.max(height, 0.01)}
                  rx={8}
                  fill={FELT.cardinal}
                  opacity={0.85}
                  filter={`url(#${ids.fuzz})`}
                  style={{transition: 'y 0.3s, height 0.3s'}}
                />
                {height > 14 && (
                  <rect
                    x={x + 5}
                    y={BAR_FLOOR - height + 5}
                    width={BAR_W - 10}
                    height={height - 10}
                    rx={5}
                    fill="none"
                    stroke="#fff"
                    strokeWidth={1.4}
                    strokeDasharray="4 3"
                    opacity={0.55}
                  />
                )}
                <text
                  x={x + BAR_W / 2}
                  y={BAR_FLOOR + 20}
                  textAnchor="middle"
                  fontSize={14}
                  fontWeight={700}
                  fill={FELT.ink}
                >
                  {index + 1}
                </text>
              </PokeGroup>
            );
          })}
          {pokedIndex !== undefined && (
            <FeltTag
              x={BAR_LEFT + pokedIndex * (BAR_W + BAR_GAP) + BAR_W / 2}
              y={
                BAR_FLOOR -
                Math.min(BAR_MAX_H, (BAR_MAX_H * pokedShare) / MAX_SHARE) -
                4
              }
              text={`${counts[pokedIndex] ?? 0} · ${Math.round(pokedShare * 100)}%`}
            />
          )}
          <path
            d={`M 14 ${shareLineY} H 290`}
            stroke={FELT.ink}
            strokeWidth={1.6}
            strokeDasharray="5 4"
            opacity={0.5}
          />
          <text
            x={290}
            y={shareLineY - 6}
            textAnchor="end"
            fontSize={12}
            fill={FELT.ink}
            opacity={0.7}
          >
            1/6
          </text>
          <path
            d={`M 14 ${BAR_FLOOR} H 290`}
            stroke={FELT.creamDark}
            strokeWidth={3}
            strokeLinecap="round"
          />
        </svg>
      </Layout>
      <ControlRow>
        <FeltButton
          type="button"
          onClick={() => {
            roll(1);
          }}
        >
          Roll
        </FeltButton>
        <FeltButton
          type="button"
          onClick={() => {
            roll(10);
          }}
        >
          Roll ×10
        </FeltButton>
        <FeltButton
          type="button"
          onClick={() => {
            roll(100);
          }}
        >
          Roll ×100
        </FeltButton>
        <FeltButton
          type="button"
          onClick={() => {
            setCounts(Array(FACES).fill(0));
          }}
        >
          Reset
        </FeltButton>
      </ControlRow>
      <Readout>
        {total} {total === 1 ? 'roll' : 'rolls'}
      </Readout>
    </FeltBoard>
  );
}
